import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
// eslint-disable-next-line
import { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
// eslint-disable-next-line
import { auth, googleProvider, db } from './firebase';
import { 
    loadQuizProgress, 
    markQuizCompleted, 
    saveSlideProgress, 
    saveQuizAnswer,
    saveQuizProgress 
} from '../utils/quizUtilityNew';
import CertificateGenerator from './CertificateGenerator';
import './PeerSupportCourse.css'; // Import your CSS styles

const PeerSupportCourse = () => {
    const [user, loading] = useAuthState(auth);
    // Debugging user state
    //const [user, loading] = [{ uid: 'dev', displayName: 'Dev User', email: 'dev@test.com' }, false]; // Mock user for development
    const [currentSlide, setCurrentSlide] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [progressLoaded, setProgressLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);
    
    // Authentication modal state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [authForm, setAuthForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [authError, setAuthError] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(false);

    // Course content slides (same as before)
    const slides = [
        {
            id: 0,
            title: "Welcome to The Free Peer Support Course",
            content: (
                <div className="text-center slide-content">
                    <img src="/assets/logo.png" alt="PilotHelper Logo" style={{maxWidth: '400px', marginBottom: '2rem', display: 'block', margin: '0 auto 2rem auto'}} />
                    <h1 className="display-4 mb-4">Welcome to The Free Peer Support Course</h1>
                </div>
            )
        },
        {
            id: 1,
            title: "Supporting the Mental Health of Pilots Through Peer Support",
            content: (
                 <div className='slide-content'>
                    <h2 className="mb-4">Supporting the Mental Health of Pilots Through Peer Support</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <h4>Overview:</h4>
                            <ol>
                                <li>Mental health is crucial to aviation safety and pilot well-being.</li>
                                <li>Pilots face unique pressures, and stigma can prevent them from seeking help.</li>
                                <li>Peer support is a confidential, supportive approach that empowers pilots to seek assistance early.</li>
                            </ol>
                            
                            <h4 className="mt-4">This module will cover:</h4>
                            <ol>
                                <li>The important role of pilots</li>
                                <li>The mental health landscape for pilots</li>
                                <li>The power of peer support</li>
                                <li>Components of successful peer support programs</li>
                                <li>Ways to take action and build a culture of support</li>
                            </ol>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/cockpitFlying.JPG" alt="Aircraft" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            title: "The Important Role of Pilots",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">The Important Role of Pilots</h2>
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <div className="mb-3 p-3 bg-warning text-dark rounded">
                                <strong>Pilots ensure passenger safety and flight efficiency</strong>
                            </div>
                            <div className="mb-3 p-3 bg-secondary text-white rounded">
                                <strong>Pilots have a vital role in global transportation and national security</strong>
                            </div>
                            <div className="mb-3 p-3 bg-warning text-dark rounded">
                                <strong>Pilots represent the highest standard of training, skill, and focus</strong>
                            </div>
                            <div className="mb-3 p-3 bg-primary text-white rounded">
                                <strong>Pilots manage disturbances, and face high cognitive demands due to complex interfaces and multitasking requirements</strong>
                            </div>
                            <div className="text-center mt-4">
                                <h3 className="text-danger">Pilot Safety and Mental Health is Essential!</h3>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/Cockpit.jpg" alt="Pilots in cockpit" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 3,
            title: "Understanding the Mental Health Landscape for Pilots",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Understanding the Mental Health Landscape for Pilots</h2>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="d-flex flex-column gap-3">
                                <div className="p-3 border rounded">
                                    <strong>Pilots encounter cognitive overload, stress from long duty hours, disrupted sleep cycles, and job-related isolation.</strong>
                                </div>
                                <div className="p-3 border rounded">
                                    <strong>Mental health issues include anxiety, depression, substance misuse, and burnout.</strong>
                                </div>
                                <div className="p-3 border rounded">
                                    <strong>Stigma often leads to underreporting of symptoms due to fear of job loss.</strong>
                                </div>
                                <div className="p-3 border rounded">
                                    <strong>These concerns, if untreated, can impact safety and crew coordination.</strong>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <img src="/assets/soloPilot.JPG" alt="Solo pilot" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 4,
            title: "Why Peer Support to Address Mental Health?",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Why Peer Support to Address Mental Health?</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <div className="d-flex flex-column gap-4">
                                <div className="d-flex align-items-center">
                                    <div className="me-3 text-warning">
                                        <i className="fas fa-helicopter fa-2x"></i>
                                    </div>
                                    <div>
                                        <strong>Shared Experience:</strong> Pilots often feel more comfortable speaking with colleagues who understand the unique stressors of aviation, such as irregular schedules, sleep disruption, and high-stakes decision-making.
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                    <div className="me-3 text-secondary">
                                        <i className="fas fa-handshake fa-2x"></i>
                                    </div>
                                    <div>
                                        <strong>Confidentiality and Trust:</strong> Most Peer Pilot Support Programs are designed to <strong>ensure anonymity</strong>, a key factor in encouraging participation.
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                    <div className="me-3 text-warning">
                                        <i className="fas fa-comments fa-2x"></i>
                                    </div>
                                    <div>
                                        <strong>Non-clinical First Contact:</strong> Peer supporters are trained to <strong>listen, assess needs, and refer</strong> pilots to clinical resources when necessary, acting as a bridge between distress and professional care.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/groupFlight.JPG" alt="Group flight" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 5,
            title: "The Power of Peer Support",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">The Power of Peer Support</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <ul className="list-unstyled">
                                <li className="mb-3">
                                    <i className="fas fa-check-circle text-success me-2"></i>
                                    <strong>Peer support allows pilots to speak with trained colleagues who understand their experiences.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-check-circle text-success me-2"></i>
                                    <strong>Peer support offers a safe, stigma-free way to discuss emotional and psychological concerns.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-check-circle text-success me-2"></i>
                                    <strong>Peer support builds trust and a culture of care within aviation organizations.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-check-circle text-success me-2"></i>
                                    <strong>Peer support, when implemented effectively, can yield positive results for peers.</strong>
                                </li>
                            </ul>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/Learning.jpg" alt="Learning session" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 6,
            title: "Key Components of a Peer Support Program",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Key Components of a Peer Support Program</h2>
                    <div className="row">
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-warning">
                                    <i className="fas fa-user-shield fa-3x"></i>
                                </div>
                                <h5>Confidentiality</h5>
                                <p>Confidentiality is the cornerstone of any effective peer support system.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-secondary">
                                    <i className="fas fa-handshake fa-3x"></i>
                                </div>
                                <h5>Training</h5>
                                <p>Peer supporters must undergo structured training to provide empathetic listening and guidance.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-warning">
                                    <i className="fas fa-handshake fa-3x"></i>
                                </div>
                                <h5>Professional Links</h5>
                                <p>Programs should link peers with mental health professionals for referrals when necessary.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-primary">
                                    <i className="fas fa-clipboard-check fa-3x"></i>
                                </div>
                                <h5>Evaluation</h5>
                                <p>Evaluation and feedback ensure program effectiveness.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-success">
                                    <i className="fas fa-user-md fa-3x"></i>
                                </div>
                                <h5>Case Study</h5>
                                <p>Lufthansa's peer program integrates clinical oversight with pilot-to-pilot support.</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4 mb-4">
                            <div className="text-center p-3 border rounded h-100">
                                <div className="mb-3 text-warning">
                                    <i className="fas fa-layer-group fa-3x"></i>
                                </div>
                                <h5>Reference</h5>
                                <p>EASA, 2019</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 7,
            title: "Building a Culture of Support",
            content: (
                <div className='slide-content'>
                    <h2 className="mb-4">Building a Culture of Support</h2>
                    <div className="row">
                        <div className="col-md-8">
                            <ul className="list-unstyled">
                                <li className="mb-3">
                                    <i className="fas fa-users text-primary me-2"></i>
                                    <strong>Leadership and crew should actively encourage open conversations about mental wellness.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-info-circle text-info me-2"></i>
                                    <strong>Promote awareness of who peer supporters are and how to access them.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-link text-success me-2"></i>
                                    <strong>Reinforce the availability of other resources like EAPs and crisis hotlines.</strong>
                                </li>
                                <li className="mb-3">
                                    <i className="fas fa-heart text-danger me-2"></i>
                                    <strong>Normalize seeking help as a sign of strength and professionalism.</strong>
                                </li>
                            </ul>
                        </div>
                        <div className="col-md-4">
                            <img src="/assets/groupFlight.JPG" alt="Community support" className="img-fluid rounded" />
                        </div>
                    </div>
                </div>
            )
        }
    ];

    const quizQuestions = [
        {
            id: 1,
            question: "What are two common challenges pilots face?",
            options: [
                "Fatigue and mental health stressors",
                "Long layovers and free travel",
                "Weather delays and hotel bookings", 
                "Flight delays and passenger complaints"
            ],
            correct: 0,
            explanation: "Pilots face unique stressors including fatigue from irregular schedules and mental health challenges from high-pressure work environments."
        },
        {
            id: 2,
            question: "Why is peer support important?",
            options: [
                "It replaces therapy",
                "It allows pilots to talk openly with trained peers who understand their experiences",
                "It helps pilots plan vacations",
                "It gives pilots a chance to socialize more"
            ],

            correct: 1,
            explanation: "Peer support is valuable because pilots can relate to colleagues who understand the unique challenges within the aviation industry."
        },
        {
            id: 3,
            question: "What is a critical peer support program feature?",
            options: [
                "Having in-flight meals",
                "Confidentiality",
                "Daily check-ins",
                "Scheduling flexibility"
            ],
            correct: 1,
            explanation: "Confidentiality is essential for building trust and encouraging pilots to seek help."
        },
        {
            id: 4,
            question: "Who offers peer support?",
            options: [
                "Family members",
                "Airline customers",
                "Airport security staff",
                "Trained pilot volunteers"
            ],
            correct: 3,
            explanation: "Peer support is most effective when provided by trained pilot volunteers who understand the aviation industry."
        },
        {
            id: 5,
            question: "What complements peer support?",
            options: [
                "Extra vacation days",
                "More flight hours",
                "Access to professional mental health services",
                "Frequent flyer rewards"
            ],
            correct: 2,
            explanation: "Peer support works best as part of a comprehensive system that includes professional mental health resources for more serious concerns."
        }
    ];

    const totalSlides = slides.length + quizQuestions.length;
    const isQuizSection = currentSlide >= slides.length;
    const currentQuizIndex = currentSlide - slides.length;

    // Authentication functions
    const closeAuthModal = () => {
        setShowAuthModal(false);
        setIsSignUp(false);
        setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
        setAuthError('');
        setIsAuthLoading(false);
    };

    const closePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setResetError('');

        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
        } catch (error) {
            let errorMessage = 'An error occurred';
            
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            setResetError(errorMessage);
        }
        
        setIsAuthLoading(false);
    };

    const handleFormChange = (e) => {
        setAuthForm({
            ...authForm,
            [e.target.name]: e.target.value
        });
        setAuthError('');
    };

    const handleGoogleSignIn = () => {
        signInWithPopup(auth, googleProvider)
            .then(() => {
                closeAuthModal();
            })
            .catch((err) => {
                console.error('Google sign in error:', err);
                setAuthError('Failed to sign in with Google. Please try again.');
            });
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setIsAuthLoading(true);
        setAuthError('');

        try {
            if (isSignUp) {
                // Sign up validation
                if (!authForm.name.trim()) {
                    setAuthError('Name is required');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password !== authForm.confirmPassword) {
                    setAuthError('Passwords do not match');
                    setIsAuthLoading(false);
                    return;
                }
                if (authForm.password.length < 6) {
                    setAuthError('Password must be at least 6 characters');
                    setIsAuthLoading(false);
                    return;
                }

                const result = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);
                
                // Update the user's profile with their name
                await updateProfile(result.user, {
                    displayName: authForm.name.trim()
                });
            } else {
                // Sign in
                await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
            }

            closeAuthModal();

            // Scroll to top after successful authentication
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            let errorMessage = 'An error occurred';
            
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            setAuthError(errorMessage);
        }
        
        setIsAuthLoading(false);
    };

    // Load user's progress when they sign in
    useEffect(() => {
        const loadUserProgress = async () => {
            if (user && !progressLoaded) {
                console.log('USER OBJECT DEBUG:', {
                    uid: user.uid,
                    displayName: user.displayName,
                    email: user.email,
                    photoURL: user.photoURL,
                    fullUser: user
                });
                console.log('Loading progress for user:', user.uid, user.displayName);
                try {
                    const progress = await loadQuizProgress(user.uid, 'free-course');
                    console.log('Progress loaded:', progress);
                    setCurrentSlide(progress.currentSlide);
                    setQuizAnswers(progress.quizAnswers);
                    setShowResults(progress.showResults);
                    setProgressLoaded(true);

                    // FORCE UPDATE USER INFO FOR EXISTING DOCUMENT
                    console.log('🔧 FORCING USER INFO UPDATE...');
                    const userDocRef = doc(db, 'quizProgress', user.uid);
                    await setDoc(userDocRef, {
                        userName: user.displayName || 'Unknown User',
                        userEmail: user.email || '',
                        lastUpdated: new Date().toISOString()
                    }, { merge: true });
                    console.log('✅ USER INFO FORCE UPDATED');

                } catch (error) {
                    console.error('Failed to load progress:', error);
                    setProgressLoaded(true); // Still mark as loaded to prevent infinite loop
                }
            }
        };

        if (!loading && user) {
            loadUserProgress();
        }
    }, [user, loading, progressLoaded]);

    // Save progress when slide changes (with debouncing)
    useEffect(() => {
        if (user && progressLoaded && currentSlide >= 0) {
            console.log('Saving slide progress:', currentSlide, 'for user:', user.displayName);
            setSaving(true);
            const timeoutId = setTimeout(async () => {
                try {
                    await saveSlideProgress(user.uid, 'free-course', currentSlide);
                } catch (error) {
                    console.error('Failed to save progress:', error);
                } finally {
                    setSaving(false);
                }
            }, 500); // Debounce saves by 500ms

            return () => {
                clearTimeout(timeoutId);
                setSaving(false);
            };
        }
    }, [currentSlide, user, progressLoaded]);

    const handleNext = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePrev = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Scroll to top whenever slide changes (after DOM update)
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentSlide]);

    const handleQuizAnswer = async (questionId, answerIndex) => {
        const question = quizQuestions.find(q => q.id === questionId);
        const isCorrect = answerIndex === question.correct;

        const newQuizAnswers = {
            ...quizAnswers,
            [questionId]: {
                selected: answerIndex,
                correct: isCorrect
            }
        };

        setQuizAnswers(newQuizAnswers);

        // Save answer to Firestore
        if (user) {
            console.log('Saving quiz answer for question', questionId, 'for user:', user.displayName);
            try {
                await saveQuizAnswer(user.uid, 'free-course', questionId, answerIndex, isCorrect);
            } catch (error) {
                console.error('Failed to save quiz answer:', error);
            }
        }

        // Check if quiz is complete
        const answeredQuestions = Object.keys(newQuizAnswers).length;

        if (answeredQuestions === quizQuestions.length) {
            setShowResults(true);
            
            // Mark quiz as completed if user passed
            const correctAnswers = Object.values(newQuizAnswers).filter(answer => answer.correct).length;
            if (user && correctAnswers === quizQuestions.length) {
                console.log('Quiz completed successfully by:', user.displayName);
                try {
                    await markQuizCompleted(user.uid, 'free-course', {
                        currentSlide,
                        quizAnswers: newQuizAnswers,
                        showResults: true
                    });
                } catch (error) {
                    console.error('Failed to mark quiz as completed:', error);
                }
            }
        }
    };

    const resetQuiz = async () => {
        setQuizAnswers({});
        setShowResults(false);
        setCurrentSlide(slides.length);
        
        // Also reset in database
        if (user) {
            try {
                await saveQuizProgress(user.uid, 'free-course', {
                    currentSlide: slides.length,
                    quizAnswers: {},
                    showResults: false,
                    completedAt: null
                }, user);
            } catch (error) {
                console.error('Failed to reset quiz in database:', error);
            }
        }
    };

    const getCorrectAnswers = () => {
        return Object.values(quizAnswers).filter(answer => answer.correct).length;
    };

    // Show loading screen while checking authentication
    if (loading) {
        return (
        <div className='peer-support-course-container'>
            <div className="container">
                <div className="loading-container">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="loading-text">Loading...</p>
                </div>
            </div>
        </div>
        );
    }

    // Show sign-in required screen with both Google and Email/Password options
    if (!user) {
        return (
            <>
            <div className='auth-screen-container'>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 text-center">
                            <img src="/assets/logo.png" alt="PilotHelper Logo" style={{maxWidth: '300px', marginBottom: '2rem', display: 'block', margin: '0 auto 2rem auto'}} />
                            <h2 className="mb-4">Sign in Required</h2>
                            <p className="lead mb-4">
                                Please sign in to access the PilotHelper Peer Support Course. 
                                Your progress will be saved and you can resume from any device.
                            </p>
                            
                            <div className="d-grid gap-2">
                                <button 
                                    className="btn btn-primary btn-lg" 
                                    onClick={() => setShowAuthModal(true)}
                                >
                                    <i className="fas fa-user me-2"></i>
                                    Sign In / Sign Up
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Authentication Modal */}
                    {showAuthModal && (
                        <div
                            className="modal show d-block"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            onClick={closeAuthModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="course-auth-modal-title"
                        >
                            <div
                                className="modal-dialog modal-dialog-centered"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content">
                                    <div className="modal-header border-0">
                                        <h5 id="course-auth-modal-title" className="modal-title">
                                            {isSignUp ? 'Create Account' : 'Sign In'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={closeAuthModal}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        {/* Google Sign In Button */}
                                        <button
                                            onClick={handleGoogleSignIn}
                                            className="btn btn-outline-dark w-100 mb-3 d-flex align-items-center justify-content-center"
                                            disabled={isAuthLoading}
                                        >
                                            <img
                                                src="https://developers.google.com/identity/images/g-logo.png"
                                                alt="Google"
                                                width="20"
                                                className="me-2"
                                            />
                                            Continue with Google
                                        </button>

                                        <div className="text-center mb-3">
                                            <span className="text-muted">or</span>
                                        </div>

                                        {/* Error Message */}
                                        {authError && (
                                            <div className="alert alert-danger" role="alert">
                                                {authError}
                                            </div>
                                        )}

                                        {/* Email/Password Form */}
                                        <form onSubmit={handleEmailAuth}>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="name" className="form-label">Full Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="name"
                                                        name="name"
                                                        value={authForm.name}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your full name"
                                                    />
                                                </div>
                                            )}
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    name="email"
                                                    value={authForm.email}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="password" className="form-label">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="password"
                                                    name="password"
                                                    value={authForm.password}
                                                    onChange={handleFormChange}
                                                    required
                                                    disabled={isAuthLoading}
                                                    minLength="6"
                                                />
                                            </div>
                                            {isSignUp && (
                                                <div className="mb-3">
                                                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        value={authForm.confirmPassword}
                                                        onChange={handleFormChange}
                                                        required
                                                        disabled={isAuthLoading}
                                                        minLength="6"
                                                    />
                                                </div>
                                            )}
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary w-100 mb-3"
                                                disabled={isAuthLoading}
                                            >
                                                {isAuthLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {isSignUp ? 'Creating Account...' : 'Signing In...'}
                                                    </>
                                                ) : (
                                                    isSignUp ? 'Create Account' : 'Sign In'
                                                )}
                                            </button>
                                        </form>

                                        {/* Forgot Password Link - Only show during sign in */}
                                        {!isSignUp && (
                                            <div className="text-center mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-decoration-none"
                                                    onClick={() => {
                                                        closeAuthModal();
                                                        setShowPasswordResetModal(true);
                                                    }}
                                                    style={{fontSize: '0.9rem'}}
                                                >
                                                    Forgot your password?
                                                </button>
                                            </div>
                                        )}

                                        {/* Toggle between sign in and sign up */}
                                        <div className="text-center">
                                            <span className="text-muted">
                                                {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => {
                                                    setIsSignUp(!isSignUp);
                                                    setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
                                                    setAuthError('');
                                                }}
                                                disabled={isAuthLoading}
                                            >
                                                {isSignUp ? 'Sign In' : 'Sign Up'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

                {/* Password Reset Modal */}
                {showPasswordResetModal && (
                    <div
                        className="modal show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        onClick={closePasswordResetModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="course-reset-modal-title"
                    >
                        <div
                            className="modal-dialog modal-dialog-centered"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 id="course-reset-modal-title" className="modal-title">
                                        Reset Password
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closePasswordResetModal}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {!resetSuccess ? (
                                        <>
                                            <p className="text-muted mb-4">
                                                Enter your email address and we'll send you a link to reset your password.
                                            </p>

                                            {/* Error Message */}
                                            {resetError && (
                                                <div className="alert alert-danger" role="alert">
                                                    {resetError}
                                                </div>
                                            )}

                                            <form onSubmit={handlePasswordReset}>
                                                <div className="mb-3">
                                                    <label htmlFor="resetEmail" className="form-label">Email Address</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="resetEmail"
                                                        value={resetEmail}
                                                        onChange={(e) => setResetEmail(e.target.value)}
                                                        required
                                                        disabled={isAuthLoading}
                                                        placeholder="Enter your email address"
                                                    />
                                                </div>
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary w-100 mb-3"
                                                    disabled={isAuthLoading}
                                                >
                                                    {isAuthLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Sending Reset Email...
                                                        </>
                                                    ) : (
                                                        'Send Reset Email'
                                                    )}
                                                </button>
                                            </form>

                                            <div className="text-center">
                                                <span className="text-muted">
                                                    Remember your password? 
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 ms-1"
                                                    onClick={() => {
                                                        closePasswordResetModal();
                                                        setShowAuthModal(true);
                                                    }}
                                                    disabled={isAuthLoading}
                                                >
                                                    Sign In
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <div className="text-success mb-3">
                                                <i className="fas fa-check-circle fa-3x"></i>
                                            </div>
                                            <h5 className="mb-3">Reset Email Sent!</h5>
                                            <p className="text-muted mb-4">
                                                We've sent a password reset link to <strong>{resetEmail}</strong>. 
                                                Check your email and follow the instructions to reset your password.
                                            </p>
                                            <p className="text-muted small mb-4">
                                            Don't see the email? <strong>Check your spam folder or try again with a different email address.</strong>
                                            </p>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={closePasswordResetModal}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    const renderSlide = () => {
        if (isQuizSection) {
            const question = quizQuestions[currentQuizIndex];
            const userAnswer = quizAnswers[question.id];

            return (
                <div>
                    <h2 className="mb-4">Question {question.id} of {quizQuestions.length}</h2>
                    <div className="card quiz-card">
                        <div className="card-body">
                            <h4 className="card-title mb-4">{question.question}</h4>
                            <div className="d-grid gap-2">
                                {question.options.map((option, index) => {
                                    let className = "btn btn-outline-primary text-start";
                                    let disabled = false;

                                    if (userAnswer) {
                                        disabled = true;
                                        if (index === question.correct) {
                                            className = "btn btn-success text-start";
                                        } else if (index === userAnswer.selected && !userAnswer.correct) {
                                            className = "btn btn-danger text-start";
                                        } else {
                                            className = "btn btn-outline-secondary text-start";
                                        }
                                    }

                                    return (
                                        <button
                                            key={index}
                                            className={className}
                                            onClick={() => handleQuizAnswer(question.id, index)}
                                            disabled={disabled}
                                        >
                                            {String.fromCharCode(97 + index)}) {option}
                                        </button>
                                    );
                                })}
                            </div>

                            {userAnswer && (
                                <div className={`alert quiz-explanation mt-4 ${userAnswer.correct ? 'alert-success' : 'alert-warning'}`}>
                                    <strong>{userAnswer.correct ? 'Correct!' : 'Incorrect.'}</strong>
                                    <p className="mb-0 mt-2">{question.explanation}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        } else {
            return slides[currentSlide].content;
        }
    };

    const renderQuizResults = () => {
        const correctAnswers = getCorrectAnswers();
        const passed = correctAnswers === quizQuestions.length;
        const allQuestionsAnswered = Object.keys(quizAnswers).length === quizQuestions.length;

        return (
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h2 className="mb-0">Quiz Results</h2>
                </div>
                <div className="card-body">
                    <div className="text-center mb-4">
                        <h3>Your Score: {correctAnswers}/{quizQuestions.length} ({Math.round((correctAnswers / quizQuestions.length) * 100)}%)</h3>
                        {passed ? (
                            <div className="alert alert-success">
                                <h4>Congratulations! 🎉</h4>
                                <p>You've successfully completed the PilotHelper Peer Support Course!</p>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={() => setShowCertificate(true)}
                                >
                                    Get Your Certificate
                                </button>
                            </div>
                        ) : (
                            <div className="alert alert-warning">
                                <h4>Almost there!</h4>
                                <p>You need to get all questions correct to pass. Please review and try again.</p>
                                <button
                                    className="btn btn-warning"
                                    onClick={resetQuiz}
                                >
                                    Retake Quiz
                                </button>
                            </div>
                        )}
                    </div>

                    {allQuestionsAnswered && (
                        <div className="quiz-review">
                            <h4>Review Your Answers:</h4>
                            {quizQuestions.map((question, index) => {
                                const userAnswer = quizAnswers[question.id];
                                return (
                                    <div key={question.id} className="question-card">
                                        <div className="question-header">
                                            Question {index + 1} of {quizQuestions.length}
                                        </div>
                                        <div className="question-body">
                                            <div className="question-text">
                                                {question.question}
                                            </div>
                                            <div className="answer-options">
                                                {question.options.map((option, optionIndex) => {
                                                    let badgeClass = "bg-secondary";

                                                    if (userAnswer && userAnswer.selected === optionIndex) {
                                                        badgeClass = userAnswer.correct ? "bg-success" : "bg-danger";
                                                    } else if (optionIndex === question.correct) {
                                                        badgeClass = "bg-success";
                                                    }

                                                    return (
                                                        <div key={optionIndex} className="answer-option">
                                                            <span className={`badge ${badgeClass} flex-shrink-0`}>
                                                                {String.fromCharCode(65 + optionIndex)}
                                                            </span>
                                                            <span>{option}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="explanation-section">
                                                <small>
                                                    <strong>Explanation:</strong> {question.explanation}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className='peer-support-course-container'>
                <div className="container">
                    <div className='course-content-wrapper'>
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                {/* User Info */}
                                <div className='user-info-section'>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <small className="text-muted">
                                                <strong>{user.displayName || user.email?.split('@')[0] || 'User'}</strong> ({user.email})
                                            </small>
                                        </div>
                                        <div>
                                            {saving ? (
                                                <small className="text-warning">
                                                    <i className="fas fa-spinner fa-spin me-1"></i>
                                                    Saving...
                                                </small>
                                            ) : (
                                                <small className="text-success">
                                                    <i className="fas fa-check me-1"></i>
                                                    Progress saved
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="course-progress-section">
                                    <div className="progress">
                                        <div 
                                            className="progress-bar" 
                                            role="progressbar" 
                                            style={{width: `${((currentSlide + 1) / totalSlides) * 100}%`}}
                                        >
                                            {Math.round(((currentSlide + 1) / totalSlides) * 100)}%
                                        </div>
                                    </div>
                                    <small className="text-muted progress-info">
                                        Slide {currentSlide + 1} of {totalSlides}
                                        {isQuizSection && " (Quiz Section)"}
                                    </small>
                                </div>

                                {/* Course Content */}
                                {showResults ? renderQuizResults() : renderSlide()}

                                {/* Navigation */}
                                {!showResults && (
                                    <div className='course-navigation'>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button 
                                                className="btn btn-secondary" 
                                                onClick={handlePrev}
                                                disabled={currentSlide === 0}
                                            >
                                                <i className="fas fa-chevron-left me-2"></i>
                                                Previous
                                            </button>
                                            
                                            <button 
                                                className="btn btn-primary" 
                                                onClick={handleNext}
                                                disabled={currentSlide === totalSlides - 1 || (isQuizSection && !quizAnswers[quizQuestions[currentQuizIndex].id])}
                                            >
                                                Next
                                                <i className="fas fa-chevron-right ms-2"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Certificate Generator Modal */}
            {showCertificate && (
                <CertificateGenerator 
                    user={user} 
                    onClose={() => setShowCertificate(false)}
                />
            )}
        </>
    );
};

export default PeerSupportCourse;